// The studio jeans repair screen (components/JeansScreen.jsx): every repair a
// pair of jeans needs in one pass, one row per hole with its own size and its
// own place. It stands in for the service and repair-detail screens when staff
// pick Repairing on a pair of jeans. Alterations keep the path they have
// always had, because they are the bulk of jeans work and almost never arrive
// with holes.
//
// Why holes and not another counter: 25 pairs of jeans in the 12 months to
// 25 Oct 2026 came in with holes of more than one size, and every size was its
// own trip back through the service screen. Only 1 of those 25 also needed an
// alteration, while 532 pairs were alterations with no hole at all. The
// "How many?" control (utils/serviceOptions.js) already covers several holes
// of the SAME size, which is why this screen is about the mix of sizes.
//
// Prices are not worked out here. Every hole is charged at its own size's
// price, however many there are (Calley, 31 Oct 2026): there is no
// each-extra price on a pair of jeans any more, so nothing tiers the second
// hole down. The screen still reads its total back out of the basket it
// would save, so what staff quote at the counter cannot drift from what the
// bill says.
//
// There is no fxryshop twin of this file. Online denim is its own screen
// (fxryshop/src/utils/denimCheckout.js), with the extra questions a customer
// who cannot show us the garment has to answer.

import { repricePairs, servicePrice } from "./cartSummary.js";
import {
  TASK_FOR_KIND,
  pairSiblings,
  repeatsOnOnePiece,
  serviceKind,
  serviceSize,
  shortServiceName,
} from "./serviceOptions.js";
import {
  CUSTOM_KINDS,
  MAX_COUNT,
  MAX_CUSTOM,
  MAX_NOTE,
  SOMEWHERE_ELSE,
  chosenCustoms,
  customsTotal,
  expandCounts,
  holeLocation,
  newCustom,
  offeredOn,
  priceOn,
  restorationPriceOf,
} from "./sweaterScreen.js";

// Shared with the sweater screen rather than written twice: a hole's place, a
// custom row and a note behave the same whatever the garment is.
export {
  CUSTOM_KINDS,
  MAX_COUNT,
  MAX_CUSTOM,
  MAX_NOTE,
  SOMEWHERE_ELSE,
  chosenCustoms,
  customsTotal,
  expandCounts,
  holeLocation,
  newCustom,
  offeredOn,
  priceOn,
  restorationPriceOf,
};

export const isJeans = (item) =>
  String(item?.name ?? item ?? "")
    .trim()
    .toUpperCase() === "JEANS";

/** Where a hole is, in the words staff use at the counter. The same list the
 *  online denim screen offers, so an order reads the same either way. */
export const HOLE_PLACES = [
  "Knee",
  "Thigh",
  "Inner thigh",
  "Crotch",
  "Seat",
  "Back pocket",
  "Front pocket",
  "Belt loop area",
  "Hem",
  "Cuff",
];

/** The studio takes jeans with a lot of holes; the cap only stops a runaway
 *  list. Up to 4 sizes on one pair is what the job history shows. */
export const MAX_HOLES = 20;

export const newHole = () => ({ size: 0, place: "", other: "", thin: null });

const HOLE = /RIP\s*\/\s*HOLE/i;
const ADDITIONAL = /\bADDITIONAL\b/i;
const CROTCH = /\bCROTCH\b/i;
const REBUILD = /\bREBUILD\b/i;

/**
 * The jeans catalogue split into what the screen asks about:
 *
 *   holes     one entry per size, smallest first. The catalogue's ADDITIONAL
 *             rows are left out: jeans holes have no each-extra price, so
 *             every hole is its own size's price (Calley, 31 Oct 2026).
 *   crotch    reinforcement and the full rebuild.
 *   extras    the rest of the repairs: the zip, a tack button, stitching.
 *
 * Alterations are left out on purpose: they stay on the service screen.
 */
export const splitJeansServices = (services) => {
  const repairs = (Array.isArray(services) ? services : []).filter(
    (service) => serviceKind(service) === "repair",
  );
  const short = (service) => shortServiceName(service?.name, "JEANS");
  const holes = repairs
    .filter((s) => HOLE.test(s?.name || "") && !ADDITIONAL.test(s?.name || ""))
    .map((service) => ({
      service,
      pair: pairSiblings(service, repairs, "JEANS"),
      size: serviceSize(short(service)),
    }))
    .sort((a, b) => (a.size ?? 0) - (b.size ?? 0));
  const crotchRows = repairs.filter((s) => CROTCH.test(s?.name || ""));
  return {
    holes,
    crotch: {
      reinforce: crotchRows.find((s) => !REBUILD.test(s?.name || "")) || null,
      rebuild: crotchRows.find((s) => REBUILD.test(s?.name || "")) || null,
    },
    extras: repairs.filter(
      (s) => !HOLE.test(s?.name || "") && !CROTCH.test(s?.name || ""),
    ),
  };
};

/** `(1-3 IN)` reads "1 to 3 inches"; `(<1 IN)` reads "Under 1 inch". */
export const holeSizeLabel = (service) => {
  const short = shortServiceName(service?.name, "JEANS");
  const inside = short.match(/\(([^)]*)\)/);
  if (!inside) return short;
  const text = inside[1].replace(/\s*IN\b\s*/gi, "").trim();
  const under = text.match(/^<\s*(\S+)/);
  if (under) return `Under ${under[1]}"`;
  const range = text.match(/^(\S+?)"?\s*[-–]\s*(\S+?)"?$/);
  return range ? `${range[1]}" to ${range[2]}"` : text;
};

/** "Replace the zip", from "JEANS - ZIPPER REPLACEMENT". */
export const extraLabel = (service) => {
  const short = shortServiceName(service?.name, "JEANS").toLowerCase();
  return short.charAt(0).toUpperCase() + short.slice(1);
};

export const isCountable = (service) => repeatsOnOnePiece(service?.name);

/** A hole here can turn out to be crotch work instead (Calley, 2 Oct 2026):
 *  one small hole in the crotch is priced as a hole, but fabric worn thin
 *  around it is reinforcement or a rebuild, and charging it per hole would
 *  undercharge work that takes an hour. Asked on the two places it can
 *  happen, never guessed from the size. */
export const CROTCH_PLACES = ["Crotch", "Inner thigh"];
export const isCrotchPlace = (place) => CROTCH_PLACES.includes(place);
export const WORN_THIN_ASK = "Is the fabric around it worn thin?";

export const asksWornThin = (hole) => isCrotchPlace(hole?.place);
export const becomesCrotch = (hole) =>
  asksWornThin(hole) && hole?.thin === "yes";

export const REINFORCE = "reinforce";
export const REBUILD_KEY = "rebuild";
/** Stretch denim is reinforced, never rebuilt: the rebuild row is the
 *  100% cotton one, and there is nothing sound to sew a new panel to on
 *  stretch (Calley, 2 Oct 2026). Staff have the jeans in their hands, so
 *  this is a note by the choice rather than a question about the fabric. */
export const CROTCH_NOTE =
  "A rebuild is for 100% cotton denim. Reinforce stretch denim.";

/** No default, the same rule the sweater screen's reknit-or-darn choice
 *  follows: reinforcement and a rebuild are $115 and $200 apart, so a
 *  forgotten click must stop Continue rather than pick the cheaper one. */
export const crotchService = (crotch, method) => {
  if (method === REBUILD_KEY) return crotch?.rebuild || null;
  if (method === REINFORCE) return crotch?.reinforce || null;
  return null;
};

/** The holes that are charged as holes, biggest first.
 *
 *  Every hole is its own size's price, so the order no longer decides what
 *  anything costs. Biggest first is kept because it is the order the lines
 *  read in on the receipt, dearest work at the top. Holes that turned out to
 *  be crotch work drop out. */
export const chargedHoles = (holes, catalogue) =>
  (holes || [])
    .map((hole, index) => ({
      ...hole,
      index,
      location: holeLocation(hole),
      row: catalogue?.holes?.[hole.size],
    }))
    .filter((hole) => hole.row && !becomesCrotch(hole))
    .sort((a, b) => (b.row.size ?? 0) - (a.row.size ?? 0) || a.index - b.index);

/** Everywhere the fabric is worn thin, for the crotch line's own location. */
export const wornThinPlaces = (holes) =>
  (holes || []).filter(becomesCrotch).map(holeLocation).filter(Boolean);

export const crotchOn = (holes, crotchTicked) =>
  crotchTicked || (holes || []).some(becomesCrotch);

/** What stops Continue, in the words the screen shows. Empty when ready. */
export const missingForContinue = ({
  holes,
  catalogue,
  crotchTicked,
  crotchMethod,
  counts,
  customs = [],
  plan,
}) => {
  const missing = [];
  const charged = chargedHoles(holes, catalogue);
  const extras = expandCounts(
    (catalogue?.extras || []).filter((s) => offeredOn(s, plan)),
    counts,
  );
  const crotch = crotchOn(holes, crotchTicked);
  const custom = chosenCustoms(customs);
  if (!charged.length && !extras.length && !crotch && !custom.length) {
    missing.push("add a hole or a repair");
  }
  if ((holes || []).some((hole) => !holeLocation(hole))) {
    missing.push("say where each hole is");
  }
  if ((holes || []).some((hole) => asksWornThin(hole) && !hole.thin)) {
    missing.push("say whether the fabric around each crotch hole is worn thin");
  }
  if (crotch && !crotchService(catalogue?.crotch, crotchMethod)) {
    missing.push("pick reinforcement or a full rebuild");
  }
  if (custom.some((c) => !c.what)) missing.push("say what the custom work is");
  if (custom.some((c) => !c.price)) missing.push("type the custom work price");
  return missing;
};

/**
 * The basket after this screen: the jeans keep the plan they were given on
 * the plan screen, and the lines this screen made last time are replaced.
 *
 * Only this screen's own lines go: `intake_screen` marks them, so an
 * alteration added to the same jeans through the service screen, and work on
 * every other garment, is left exactly where it was. Coming back in and
 * pressing Continue again therefore does not double the holes.
 *
 * Lines are shaped like the ones the service and repair-detail screens build,
 * so the basket, checkout and order creation read them unchanged.
 */
export const INTAKE_SCREEN = "jeans";

/** Every line this screen makes, in the order they go into the basket. */
export const jeansLines = ({
  garment,
  plan,
  holes,
  catalogue,
  crotchTicked,
  crotchMethod,
  counts,
  notes = "",
  customs = [],
}) => {
  const line = (service, repair, more = {}) => ({
    ...service,
    selectedOption: service.name,
    selectedService: TASK_FOR_KIND[serviceKind(service)] || "Repairing",
    item_details: garment,
    repair_details: {
      location: repair?.location || "",
      description: repair?.description || "",
    },
    intake_screen: INTAKE_SCREEN,
    ...more,
  });

  const holeLines = chargedHoles(holes, catalogue).map((hole) =>
    line(
      hole.row.service,
      { location: hole.location },
      // `pairSiblings` returns null for a jeans hole, so there is no pair to
      // carry and nothing re-prices a second hole. Kept as a spread rather
      // than dropped, so a family that does pair still works here.
      hole.row.pair ? { pair: hole.row.pair } : {},
    ),
  );

  const crotchLines = [];
  if (crotchOn(holes, crotchTicked)) {
    const service = crotchService(catalogue?.crotch, crotchMethod);
    const worn = wornThinPlaces(holes);
    if (service) {
      crotchLines.push(
        line(service, {
          location: [...new Set(["Crotch", ...worn])].join(", "),
          description: worn.length ? "Fabric worn thin" : "",
        }),
      );
    }
  }

  const extraLines = expandCounts(
    (catalogue?.extras || []).filter((service) => offeredOn(service, plan)),
    counts,
  ).map((service) => line(service, null));

  const customLines = chosenCustoms(customs).map((custom, index) =>
    line(
      {
        id: `custom-${garment?.id}-${index}`,
        name: custom.what,
        standard_price: custom.price,
        premium_price: custom.price,
        is_standard: true,
        is_premium: true,
        service_type: custom.kind === "alteration",
        service_kind: custom.kind,
        is_custom: true,
        // What staff typed is the reason for the price, so the studio
        // checkout does not ask for one again.
        override_reason: `Jeans custom ${custom.kind}: ${custom.what}`,
      },
      null,
    ),
  );

  const lines = [...holeLines, ...crotchLines, ...extraLines, ...customLines];
  // The note goes on the first line only, where the other screens put
  // item_notes ("Notes to tailors"), so it shows once.
  const note = String(notes || "").trim();
  if (note && lines.length) {
    lines[0] = { ...lines[0], item_notes: note };
  }
  return lines;
};

/** The garment's lines that this screen did not make, plus every other
 *  garment's: what Continue keeps. */
const keptServices = (cart, garmentId) =>
  (cart?.services || []).filter(
    (service) =>
      service?.item_details?.id !== garmentId ||
      service?.intake_screen !== INTAKE_SCREEN,
  );

/**
 * The basket after this screen. The jeans keep the plan they were given on
 * the plan screen, and the lines this screen made last time are replaced.
 *
 * Only this screen's own lines go: `intake_screen` marks them, so an
 * alteration added to the same jeans through the service screen, and work on
 * every other garment, is left exactly where it was. Coming back in and
 * pressing Continue again therefore does not double the holes.
 *
 * Lines are shaped like the ones the service and repair-detail screens build,
 * so the basket, checkout and order creation read them unchanged.
 *
 * The lines still go through the cart's own re-pricing before they are
 * saved, which is what the service screen does too (Alteration.jsx): the
 * cart holds the row that is actually charged, and nothing downstream
 * re-prices it. Jeans holes have no each-extra price, so that pass leaves
 * them alone; it still matters for every other family in the basket.
 */
export const jeansBasket = (cart, args) => {
  const garment = (cart?.itemsData || []).find(
    (item) => item?.id === args.garmentId,
  );
  const itemsData = cart?.itemsData || [];
  return {
    itemsData,
    services: repricePairs({
      itemsData,
      services: [
        ...keptServices(cart, args.garmentId),
        ...jeansLines({ ...args, garment }),
      ],
    }).services,
  };
};

/**
 * What this screen is about to put on the bill: the price of each line it
 * made, and their total, read straight off the basket it would save.
 *
 * Read back rather than added up here, so there is one answer to "what does
 * a hole cost" and it is the one the order gets.
 */
export const jeansQuote = (cart, args) => {
  const garment = (cart?.itemsData || []).find(
    (item) => item?.id === args.garmentId,
  );
  const made = jeansLines({ ...args, garment }).length;
  if (!made) return { prices: [], total: 0 };
  const { services } = jeansBasket(cart, args);
  const prices = services
    .slice(services.length - made)
    .map((service) => servicePrice(service, args.plan));
  return { prices, total: prices.reduce((sum, price) => sum + price, 0) };
};
